import { Box, Grid, Stack, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Dispatch, SetStateAction } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import {
  ClientDTO,
  saveOurEntityClient,
  unAssignClient,
  assignClient,
  saveActivityLog,
  LogAction,
  LogEntityType,
  LogContextType,
} from 'wisestep-api';
import {
  WsDataTable,
  WsButton,
  WsTypography,
  WsTypoColors,
  getFormatDate,
  WsTypoVariants,
} from 'wisestep-base';

import { activityLogDTOHelper } from '../../../features/activityLog/ActivityHelper';
import { success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

interface ClientEntityProps {
  ourEntityId?: string;
  selectedRows?: number[];
  setSelectedRows: (indices: number[]) => void;
  ourClientListData: ClientDTO[];
  loadTabData?: () => void;
  listPage?: boolean;
  showModal?: Dispatch<SetStateAction<boolean>>;
  type?: 'ASSIGNED' | 'NOT_ASSIGNED';
  tabData?: () => void;
  getPreSelectedRow?: number[];
}

const OurEntityClient = ({
  ourEntityId,
  selectedRows = [],
  setSelectedRows,
  ourClientListData = [],
  tabData,
  listPage = false,
  type,
  loadTabData,
  getPreSelectedRow,
}: ClientEntityProps) => {
  const navigate = useNavigate();
  const setNotification = useNotificationStore((state) => state.setNotification);

  const columns = [
    {
      Header: 'Client Name',
      accessor: 'name',
      Cell: (cell: Cell) => (
        <Link
          component={RouterLink}
          underline="none"
          to={`/organisationEntity/client-details?clientId=${ourClientListData[cell.row.index].id}`}
          target="_blank"
        >
          <WsTypography variant={WsTypoVariants.SUB_TITLE} bold cursor="pointer">
            {ourClientListData[cell.row.index].name}
          </WsTypography>
        </Link>
      ),
    },
    {
      Header: 'Child Entity',
      accessor: 'childCount',
    },
    {
      Header: 'Client Location',
      accessor: 'country',
      Cell: (cell: Cell) => {
        let location: string | null = null;
        if (cell.row.original['branch']) {
          location = cell.row.original['branch'];
        }
        return <>{location}</>;
      },
    },
    {
      Header: 'Created On',
      accessor: 'createdOn',
      Cell: (cell: Cell) => getFormatDate(cell.value),
    },
    {
      Header: 'Status',
      accessor: 'isActive',
      Cell: (cell: Cell) => <>{cell.value ? 'Active' : 'In Active'}</>,
    },
    {
      Header: 'Owner',
      accessor: 'createdByName',
    },
    {
      Header: 'Received',
      accessor: 'isReceived',
      Cell: (cell: Cell) => (
        <WsTypography bold={true} color={WsTypoColors.SECONDARY}>
          {cell.value}
        </WsTypography>
      ),
    },
    {
      Header: 'Submitted',
      accessor: 'submittedCount',
      Cell: (cell: Cell) => (
        <WsTypography bold={true} color={WsTypoColors.SECONDARY}>
          {cell.value}
        </WsTypography>
      ),
    },
    {
      Header: 'Placed',
      accessor: 'placedCount',
      Cell: (cell: Cell) => (
        <WsTypography bold={true} color={WsTypoColors.SECONDARY}>
          {cell.value}
        </WsTypography>
      ),
    },
  ];
  const DrawerFooter = styled(Grid)(() => ({
    display: 'flex',
    alignItems: 'center',
    position: 'fixed',
    bottom: 1,
    left: 2,
    right: 2,
    justifyContent: 'flex-end',
  }));

  const onContinue = async () => {
    const clientIds: string[] = [];
    const clientNames: string[] = [];
    const avPayload = { ...activityLogDTOHelper };

    selectedRows.length > 0 &&
      selectedRows.forEach((index) => {
        if (ourClientListData[index]?.id) {
          clientIds.push(ourClientListData[index]?.id ?? '');
          clientNames.push(ourClientListData[index]?.name ?? '');
        }
      });
    if (type) {
      if (type == 'ASSIGNED') {
        await unAssignClient(clientIds, ourEntityId ?? '');
        avPayload.description = `UNASSIGNED ${clientNames.toString()} as Client under`;
        avPayload.action = LogAction.UnAssigned;
      } else if (type == 'NOT_ASSIGNED') {
        await assignClient(clientIds, ourEntityId ?? '');
        avPayload.action = LogAction.Assigned;
        avPayload.description = `ASSIGNED ${clientNames.toString()} as Client under`;
      } else {
        avPayload.description = `ADDED ${clientNames.toString()} as Client under`;
      }
    } else {
      await saveOurEntityClient(clientIds, ourEntityId ?? '');
    }

    if (selectedRows.toString() !== getPreSelectedRow?.toString()) {
      const clientAdded: string[] = [];
      const clientRemoved: string[] = [];

      const differenceSelectedRows = selectedRows.filter(
        (element) => !getPreSelectedRow?.includes(element)
      );

      differenceSelectedRows.forEach((index) => {
        clientAdded.push(ourClientListData[index]?.name ?? '');
      });

      const differenceGtPreSelectedRow = getPreSelectedRow?.filter(
        (element) => !selectedRows.includes(element)
      );

      differenceGtPreSelectedRow?.forEach((index) => {
        clientRemoved.push(ourClientListData[index]?.name ?? '');
      });

      const clientAddList = clientAdded.length > 0 ? `ADDED ${clientAdded.toString()}` : '';
      const clientRemoveList =
        clientRemoved.length > 0 ? `REMOVED ${clientRemoved.toString()}` : '';
      avPayload.entityType = LogEntityType.Client;
      avPayload.parentEntitiyType = LogContextType.OurEntity;
      avPayload.parentEntitiyId = ourEntityId as string;
      avPayload.field = `Client`;
      avPayload.contextData = { ourEntityId: ourEntityId };
      avPayload.description = `${clientAddList} ${clientRemoveList} as Client under`;
      clientIds.length > 0 ? await saveActivityLog(avPayload) : '';
    }

    setNotification(success('Our Entity Client saved successfully.'));

    if (!listPage) navigate(`/organisationEntity/${ourEntityId}/details`);
    else {
      tabData && tabData();
      loadTabData && loadTabData();
    }
  };
  const onRowsChoose = (indices: number[]) => setSelectedRows(indices);

  return (
    <Box>
      <Grid container py={1}>
        <WsDataTable
          columns={columns}
          data={ourClientListData as []}
          selectable={true}
          getSelectedRows={onRowsChoose}
          pageable={true}
          totalDataItems={ourClientListData.length}
          preSelectedRows={!listPage ? selectedRows : []}
        />
      </Grid>
      {type ? (
        <DrawerFooter>
          <Stack
            p={1}
            spacing={2}
            direction="row"
            sx={{ display: 'flex', justifyContent: 'right' }}
          >
            <WsButton variant="contained" size="medium" onClick={onContinue}>
              {type == 'ASSIGNED' ? 'Un Assign' : 'Assign'}
            </WsButton>
          </Stack>
        </DrawerFooter>
      ) : (
        <Stack p={1} spacing={2} direction="row" sx={{ display: 'flex', justifyContent: 'right' }}>
          <WsButton variant="contained" size="medium" onClick={onContinue}>
            Continue
          </WsButton>
        </Stack>
      )}
    </Box>
  );
};

export default OurEntityClient;
